import { useState, useEffect, useMemo } from 'react';

import { dataGridPremiumColumns } from '../../utils/helper';
import { DatagridPremiumUi, GridCellExpand } from '../Interface';
import { DocStatusList } from '../../config/document';
import documentService from '../../service/newDoc.service';
import dayjs from 'dayjs';
import { Box, Checkbox, Chip, CircularProgress, Tooltip } from '@mui/material';
import { DocumentDirection } from '../../config/DocumentDirection';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import useParam from '../../context/ParamContext';
import { formatCurrency, getRounding } from '../../utils/currencyHelper';
import useGenerateJokerReportCols from '../../hooks/useGenerateJokerReportCols';
import useConfig from '../../context/ConfigContext';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

const DocumentReports = ({ docState }) => {
  const {
    setRefetchTable,
    refetchTable,
    viewField,
    setSelectedValues,
    operatorsDisabled,
    setRowSelectionModel,
    autocompleteOptions,
    autocompleteData,
    datagridIdentifiers,
    rowSelectionModel,
    docTypeId,
    documentData,
    jokerFields,
  } = docState;

  const [reports, setReports] = useState({ rows: [], rowCount: 0 });
  const [pendingSettlementDocIds, setPendingSettlementDocIds] = useState([]);
  const { params } = useParam();
  const { config } = useConfig();

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'docNum', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  const getRowRounding = (row) => {
    const currency = autocompleteData.currency.find((c) => c.code === row.currencyCode);

    return getRounding(documentData.direction, currency);
  };

  const getOwnRowRounding = () => {
    const currency = autocompleteData.currency.find((c) => c.code === config.currencyCode);
    return getRounding(documentData.direction, currency);
  };

  useEffect(() => {
    if (operatorsDisabled) {
      setRowSelectionModel([]);
    }
  }, [operatorsDisabled]);

  const getList = (gridState) => {
    documentService.getDocuments(gridState, docTypeId).then((data) => {
      setReports(data);
    });
  };

  const jokerCols = useGenerateJokerReportCols(jokerFields);

  const renderCellExpand = (data) => {
    return <GridCellExpand value={data.value || ''} width={data.colDef.computedWidth} />;
  };

  const isSettlementPending = (docId) => pendingSettlementDocIds.includes(docId);

  const handleAutoSettlementChange = async (event, row) => {
    event.stopPropagation();

    if (!row || row.balanceSettled || isSettlementPending(row.docId)) {
      return;
    }

    setPendingSettlementDocIds((prev) => [...prev, row.docId]);

    try {
      const updatedRow = await documentService.autoCreateSettlement({
        docId: row.docId,
        docTypeId: row.docTypeId ?? docTypeId,
      });

      setReports((prev) => ({
        ...prev,
        rows: prev.rows.map((reportRow) =>
          reportRow.docId === row.docId ? { ...reportRow, ...updatedRow } : reportRow
        ),
      }));

      if (rowSelectionModel?.includes?.(row.docId)) {
        setSelectedValues(updatedRow);
      }

      setRefetchTable(true);
      addNotification({
        content: 'A kiegyenlítés sikeresen létrejött.',
        type: NotificationType.SUCCESS,
      });
    } catch (_error) {
      addNotification({
        content: 'A kiegyenlítés automatikus létrehozása nem sikerült.',
        type: NotificationType.ERROR,
      });
    } finally {
      setPendingSettlementDocIds((prev) => prev.filter((docId) => docId !== row.docId));
    }
  };

  const renderSettlementStatusCell = (data) => {
    const pending = isSettlementPending(data.row.docId);
    const isSettled = Boolean(data.row.balanceSettled);

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minWidth: 'fit-content',
            flexShrink: 1,
          }}
        >
          {isSettled ? (
            <Chip label="Kiegyenlített" color="success" size="small" variant="outlined" />
          ) : (
            <Chip
              label={`${documentData.direction === DocumentDirection.INCOMING ? 'Követel' : 'Tartozik'}`}
              color="error"
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: 'auto',
            flexShrink: 0,
          }}
        >
          {pending ? (
            <CircularProgress size={18} />
          ) : (
            <Tooltip
              title={
                isSettled
                  ? 'A már kiegyenlített bizonylat itt nem módosítható.'
                  : 'Automatikus kiegyenlítés létrehozása'
              }
            >
              <span>
                <Checkbox
                  size="small"
                  checked={isSettled}
                  disabled={isSettled}
                  sx={{ p: 0 }}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  onChange={(event) => handleAutoSettlementChange(event, data.row)}
                />
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  const showCompanyColumns = Boolean(documentData?.company || documentData?.cooperation);

  const columns = useMemo(() => {
    return dataGridPremiumColumns([
      {
        field: 'docNum',
        headerName: 'Bizonylatszám',
        type: 'string',
      },
      {
        field: 'docStatus',
        headerName: 'Státusz',
        type: 'singleSelect',
        sortable: false,
        valueOptions: DocStatusList,
      },
      ...(showCompanyColumns
        ? [
            {
              field: 'companyId',
              headerName: 'Cég',
              type: 'singleSelect',
              sortable: false,
              renderCell: (data) => {
                return data.row.company?.code;
              },
              valueOptions: autocompleteOptions.company,
            },
            {
              field: 'companyName',
              headerName: 'Cég neve',
              type: 'string',
            },
          ]
        : []),
      {
        field: 'docDate',
        headerName: 'Teljesítés dátuma',
        type: 'date',
        valueGetter: (_data, row) => {
          return new Date(row.docDate);
        },
        renderCell: (data) => {
          return dayjs(data.row.docDate).format('YYYY-MM-DD');
        },
      },
      {
        field: 'issueDate',
        headerName: 'Kiállítás dátuma',
        type: 'date',
        valueGetter: (_data, row) => {
          return new Date(row.issueDate);
        },
        renderCell: (data) => {
          return dayjs(data.row.issueDate).format('YYYY-MM-DD');
        },
      },

      {
        field: 'dueDate',
        headerName: 'Határidő',
        type: 'date',
        valueGetter: (_data, row) => {
          return new Date(row.dueDate);
        },
        renderCell: (data) => {
          return dayjs(data.row.dueDate).format('YYYY-MM-DD');
        },
      },
      ...(documentData?.employee
        ? [
            {
              field: 'employeeId',
              headerName: 'Dolgozó',
              type: 'singleSelect',
              sortable: false,
              renderCell: (data) => {
                return data.row.employee?.name;
              },
              valueOptions: autocompleteOptions.employee,
            },
          ]
        : []),
      ...(documentData?.project
        ? [
            {
              field: 'projectId',
              headerName: 'Projekt',
              type: 'string',
              sortable: false,
              renderCell: (data) => {
                return data.row.project?.projectName;
              },
            },
            {
              field: 'projectOrderNumber',
              headerName: getProjectOrderNumberName(params),
              type: 'string',
              sortable: false,
              renderCell: (data) => {
                return data.row.project?.orderNumber;
              },
            },
          ]
        : []),
      {
        field: 'reference',
        headerName: 'Hivatkozási szám',
        type: 'string',
      },
      {
        field: 'costCenterCode',
        headerName: 'Költséghely',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.costCenter?.name;
        },
        valueOptions: autocompleteOptions.costCenter,
      },
      ...(documentData?.enableSettlement
        ? [
            {
              field: 'balanceSettled',
              headerName: 'Kintlévőség',
              type: 'singleSelect',
              sortable: false,
              align: 'right',
              headerAlign: 'right',
              renderCell: renderSettlementStatusCell,
              valueOptions: [
                { value: true, label: 'Kiegyenlített' },
                {
                  value: false,
                  label: documentData.direction === DocumentDirection.INCOMING ? 'Követel' : 'Tartozik',
                },
              ],
            },
            {
              field: 'settledAmount',
              headerName: 'Eddig fizetve',
              type: 'number',
              renderCell: (data) => {
                return formatCurrency(data.row.settledAmount);
              },
            },
            {
              field: 'openAmount',
              headerName: 'Nyitott összeg',
              type: 'number',
              renderCell: (data) => {
                return formatCurrency(data.row.openAmount, getRowRounding(data.row));
              },
            },
          ]
        : []),
      {
        field: 'currencyCode',
        headerName: 'Pénznem',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.currencyCode;
        },
        valueOptions: autocompleteOptions.currency,
      },
      {
        field: 'netInDocCur',
        headerName: 'Összesen nettó',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.netInDocCur, getRowRounding(data.row));
        },
      },
      {
        field: 'VATInDocCur',
        headerName: 'Összesen Áfa',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.VATInDocCur, getRowRounding(data.row));
        },
      },
      {
        field: 'sumInDocCur',
        headerName: 'Összesen bruttó',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.sumInDocCur, getRowRounding(data.row));
        },
      },
      {
        field: 'netInOwnCur',
        headerName: 'Összesen nettó saját pénznemben',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.netInOwnCur, getOwnRowRounding());
        },
      },
      {
        field: 'VATInOwnCur',
        headerName: 'Összesen Áfa saját pénznemben',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.VATInOwnCur, getOwnRowRounding());
        },
      },
      {
        field: 'sumInOwnCur',
        headerName: 'Összesen bruttó saját pénznemben',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.sumInOwnCur, getOwnRowRounding());
        },
      },
      {
        field: 'note',
        headerName: 'Megjegyzés',
        type: 'string',
        renderCell: renderCellExpand,
      },
      ...jokerCols,
    ]);
  }, [autocompleteOptions, documentData, jokerCols, showCompanyColumns, pendingSettlementDocIds]);

  return (
    <>
      {Object.keys(autocompleteOptions).length > 0 && Object.keys(documentData).length > 0 && (
        <DatagridPremiumUi
          key={datagridIdentifiers.doc}
          datagridIdentifier={datagridIdentifiers.doc}
          datagridInitialState={initialState}
          dataLoader={getList}
          rows={reports.rows}
          refetch={{ refetchTable, setRefetchTable }}
          rowCount={reports.rowCount}
          columns={[...columns]}
          onRowClick={(rowData) => {
            if (!viewField) return;
            setSelectedValues(rowData.row);
          }}
          paginationAndfilterMode="server"
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          disableRowSelectionOnClick={!viewField}
          getRowId={(row) => row.docId}
        />
      )}
    </>
  );
};

export default DocumentReports;
